import React, { useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { Tooltip } from '../Tooltip/Tooltip';
import { useTheme } from '../../../../core/providers/ThemeProvider';
import { notificationService } from '../../../utils/notificationService';
import RoleSwitchDialog from '../Dialog/RoleSwitchDialog';
import { colors } from '../../../styles/theme/colors';

interface RoleToggleProps {
  currentRole: 'traveller' | 'sender' | '';
  onChange: (newRole: 'traveller' | 'sender') => void;
  onRoleSwitched?: (newRole: 'traveller' | 'sender') => void;
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  tooltipTitle?: string;
}

const RoleToggle: React.FC<RoleToggleProps> = ({
  currentRole,
  onChange,
  onRoleSwitched,
  className,
  disabled = false,
  loading = false,
  tooltipTitle,
}) => {
  const { theme } = useTheme();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [targetRole, setTargetRole] = useState<'traveller' | 'sender'>('traveller');

  const isTraveller = currentRole === 'traveller';
  const isSender = currentRole === 'sender';
  const isDisabled = disabled || loading || !currentRole;

  // Colors based on design specs - use specific colors for each role
  const travellerColor = colors.traveller.primary; // #9B1B5A
  const senderColor = colors.sender.primary; // #7A0056

  // Background color adapts based on role: lighter shade of primary color
  const lightBg = isTraveller ? '#F5E8EF' : '#F3E5EE'; // Slightly different for sender
  const inactiveIconColor = '#484A54';
  const disabledBg = '#F3F4F6';
  const disabledColor = '#9CA3AF';

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isDisabled) {
      // Determine target role
      const newRole = isTraveller ? 'sender' : 'traveller';
      setTargetRole(newRole);
      setDialogOpen(true);
    }
  };

  const handleConfirmSwitch = () => {
    setDialogOpen(false);

    // Capture the current role BEFORE switching
    const roleBeforeSwitch = currentRole as 'traveller' | 'sender';

    // Switch to the new role
    onChange(targetRole);

    // Add notification with the role we're switching TO (so it appears in that role's notifications)
    notificationService.addSuccessNotification(
      'Role Switched',
      `You have successfully switched to ${targetRole === 'traveller' ? 'Traveler' : 'Sender'} mode.`,
      {
        user_role: targetRole, // Explicitly set the role to the NEW role
      }
    );

    // Trigger notification callback if provided
    if (onRoleSwitched) {
      onRoleSwitched(targetRole);
    }
  };

  const handleCancelSwitch = () => {
    setDialogOpen(false);
    // Keep current role, no notification needed
  };

  const toggleContent = (
    <Box
      className={className}
      onClick={handleClick}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '2px',
        width: '132px',
        height: '40px',
        background: isDisabled ? disabledBg : lightBg,
        borderRadius: '30px',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        userSelect: 'none',
        position: 'relative',
        opacity: isDisabled ? 0.6 : 1,
        transition: 'opacity 0.2s ease',
        '&:hover': {
          opacity: isDisabled ? 0.6 : 0.9,
        },
      }}
      role="button"
      aria-label={`Switch to ${isTraveller ? 'sender' : 'traveller'} mode`}
      aria-disabled={isDisabled}
    >
      {/* Loading Overlay */}
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            borderRadius: '30px',
            zIndex: 10,
          }}
        >
          <CircularProgress size={20} sx={{ color: isTraveller ? travellerColor : senderColor }} />
        </Box>
      )}

      {/* Traveller Button/Icon */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          padding: '8px',
          gap: '4px',
          width: isTraveller ? '90px' : '36px',
          height: '36px',
          background: isTraveller
            ? (isDisabled ? disabledColor : travellerColor)
            : lightBg,
          borderRadius: '100px',
          transition: 'all 0.3s ease',
          flex: 'none',
          order: 0,
          flexGrow: 0,
        }}
      >
        {/* Plane Icon */}
        <Box
          sx={{
            width: '20px',
            height: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14.8333 16L13.3333 9.16667L16.25 6.25C17.5 5 17.9167 3.33333 17.5 2.5C16.6667 2.08333 15 2.5 13.75 3.75L10.8333 6.66667L4 5.16667C3.58333 5.08333 3.25 5.25 3.08333 5.58333L2.83333 6C2.66667 6.41667 2.75 6.83333 3.08333 7.08333L7.5 10L5.83333 12.5H3.33333L2.5 13.3333L5 15L6.66667 17.5L7.5 16.6667V14.1667L10 12.5L12.9167 16.9167C13.1667 17.25 13.5833 17.3333 14 17.1667L14.4167 17C14.75 16.75 14.9167 16.4167 14.8333 16Z"
              stroke={isTraveller ? '#FFFFFF' : (isDisabled ? disabledColor : inactiveIconColor)}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Box>

        {/* Traveller Text */}
        {isTraveller && (
          <Typography
            sx={{
              fontFamily: 'Poppins, sans-serif',
              fontStyle: 'normal',
              fontWeight: 500,
              fontSize: '12px',
              lineHeight: '18px',
              color: '#FFFFFF',
              flex: 'none',
              order: 1,
              flexGrow: 0,
            }}
          >
            Traveler
          </Typography>
        )}
      </Box>

      {/* Sender Button/Icon */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          padding: '8px',
          gap: '10px',
          width: isSender ? '90px' : '36px',
          height: '36px',
          background: isSender
            ? (isDisabled ? disabledColor : senderColor)
            : lightBg,
          borderRadius: '100px',
          transition: 'all 0.3s ease',
          flex: 'none',
          order: 1,
          flexGrow: 0,
          marginLeft: '4px',
        }}
      >
        {/* Package Icon */}
        <Box
          sx={{
            width: '20px',
            height: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2.5 5.83333L10 2.5L17.5 5.83333M2.5 5.83333L10 9.16667M2.5 5.83333V14.1667L10 17.5M17.5 5.83333L10 9.16667M17.5 5.83333V14.1667L10 17.5M10 9.16667V17.5"
              stroke={isSender ? '#FFFFFF' : (isDisabled ? disabledColor : inactiveIconColor)}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Box>

        {/* Sender Text */}
        {isSender && (
          <Typography
            sx={{
              fontFamily: 'Poppins, sans-serif',
              fontStyle: 'normal',
              fontWeight: 500,
              fontSize: '12px',
              lineHeight: '18px',
              color: '#FFFFFF',
              flex: 'none',
              order: 1,
              flexGrow: 0,
            }}
          >
            Sender
          </Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      {isDisabled && !loading ? (
        <Tooltip
          message={tooltipTitle ?? (!currentRole ? 'Please log in to switch roles' : 'Role switching disabled')}
          useTranslation={false}
        >
          {toggleContent}
        </Tooltip>
      ) : (
        toggleContent
      )}

      {/* Role Switch Dialog */}
      <RoleSwitchDialog
        open={dialogOpen}
        onClose={handleCancelSwitch}
        onConfirm={handleConfirmSwitch}
        targetRole={targetRole}
        loading={false}
        primaryColor={targetRole === 'traveller' ? travellerColor : senderColor}
        primaryHoverColor={targetRole === 'traveller' ? travellerColor : senderColor}
      />
    </>
  );
};

export default RoleToggle;